// checkInService

import {
  collection,
  doc,
  getDocs,
  updateDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";

import { db } from "../../../config/firebase";
import { REGISTRATION_COLLECTION } from "../../registration/constants/registrationConstants";
import { sendCheckInToGoogleSheet } from "../../../services/googleSheets/googleSheetService";

import {
  CHECK_IN_METHOD,
  CHECK_IN_STATUS,
  DEFAULT_CHECKED_IN_BY,
} from "../constants/checkInConstants";

export async function searchRegistrationsForCheckIn(searchText) {
  const normalizedSearch = searchText.trim().toLowerCase();

  if (!normalizedSearch) {
    return {
      success: true,
      data: [],
    };
  }

  try {
    const registrationsRef = collection(db, REGISTRATION_COLLECTION);
    const snapshot = await getDocs(registrationsRef);

    const results = snapshot.docs
      .map((docItem) => ({
        id: docItem.id,
        ...docItem.data(),
      }))
      .filter((registration) => {
        const registrationId = registration.id?.toLowerCase() || "";
        const fullName = registration.fullName?.toLowerCase() || "";
        const email = registration.email?.toLowerCase() || "";
        const phone = registration.phone?.toLowerCase() || "";
        const organizationName =
          registration.organizationName?.toLowerCase() || "";

        return (
          registrationId.includes(normalizedSearch) ||
          fullName.includes(normalizedSearch) ||
          email.includes(normalizedSearch) ||
          phone.includes(normalizedSearch) ||
          organizationName.includes(normalizedSearch)
        );
      })
      .slice(0, 10);

    return {
      success: true,
      data: results,
    };
  } catch (error) {
    console.error("Check-in search error:", error);

    return {
      success: false,
      data: [],
      message: "Could not search registrations.",
    };
  }
}

export async function markRegistrationAsCheckedIn(
  registration,
  checkedInBy = DEFAULT_CHECKED_IN_BY
) {
  if (!registration?.id) {
    return {
      success: false,
      message: "Invalid registration selected.",
    };
  }

  if (registration.checkedIn) {
    return {
      success: false,
      alreadyCheckedIn: true,
      message: "This guest is already checked in.",
    };
  }

  try {
    const registrationRef = doc(db, REGISTRATION_COLLECTION, registration.id);

    await updateDoc(registrationRef, {
      checkedIn: true,
      checkedInAt: serverTimestamp(),
      checkedInBy,
      checkInMethod: CHECK_IN_METHOD.MANUAL_SEARCH,
      checkInStatus: CHECK_IN_STATUS.CHECKED_IN,
    });

    const checkInRecord = {
      registrationId: registration.id,
      fullName: registration.fullName || "",
      email: registration.email || "",
      phone: registration.phone || "",
      organizationType: registration.organizationType || "",
      organizationName: registration.organizationName || "",
      college: registration.college || "",
      department: registration.department || "",
      role: registration.role || "",
      invitationCode: registration.invitationCode || "",
      checkInMethod: CHECK_IN_METHOD.MANUAL_SEARCH,
      checkedInBy,
      checkInStatus: CHECK_IN_STATUS.CHECKED_IN,
    };

    const sheetResponse = await sendCheckInToGoogleSheet(checkInRecord);

    if (!sheetResponse.success) {
      console.warn("Check-in Google Sheet sync warning:", sheetResponse.message);
    }

    return {
      success: true,
      message: `${registration.fullName} has been checked in successfully.`,
      data: {
        ...registration,
        checkedIn: true,
        checkedInBy,
        checkInMethod: CHECK_IN_METHOD.MANUAL_SEARCH,
        checkInStatus: CHECK_IN_STATUS.CHECKED_IN,
        checkedInAt: new Date().toISOString(),
      },
    };
  } catch (error) {
    console.error("Check-in update error:", error);

    return {
      success: false,
      message: "Could not check in this guest.",
    };
  }
}

export async function getRecentCheckIns() {
  try {
    const registrationsRef = collection(db, REGISTRATION_COLLECTION);

    const q = query(
      registrationsRef,
      where("checkedIn", "==", true),
      orderBy("checkedInAt", "desc"),
      limit(10)
    );

    const snapshot = await getDocs(q);

    const data = snapshot.docs.map((docItem) => ({
      id: docItem.id,
      ...docItem.data(),
    }));

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error("Recent check-ins error:", error);

    return {
      success: false,
      data: [],
      message: "Could not load recent check-ins.",
    };
  }
}